import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { friendlyError } from '@/lib/errorMessage';
import { Outlet } from 'react-router';
import { App as AntApp } from 'antd';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setOrganizations,
  setMyInvitations,
  setMembers,
  setOrgInvitations,
  setTagDefinitions,
  setLoading,
  setError,
} from '@/store/organizationSlice';
import { setPendingSetups, setSigningRequests } from '@/store/vaultSlice';
import { organizationsApi } from '@/api/organizations';
import { vaultsApi } from '@/api/vaults';
import { canInvite } from '@/types/organization';
import { useResponsive } from '@/hooks/useResponsive';
import { PastDueBanner } from '@/components/common/PastDueBanner';
import { DemoBanner } from '@/demo/DemoBanner';
import { WalletConnectProvider } from '@/contexts/WalletConnectContext';
import { WalletConnectModals } from '@/components/walletconnect';
import '@/styles/layout.css';

export function AppLayout() {
  const { t } = useTranslation();
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const dispatch = useAppDispatch();
  const { isMobile } = useResponsive();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const currentOrgId = useAppSelector((state) => state.organization.currentOrgId);
  const organizations = useAppSelector((state) => state.organization.organizations);
  const orgDataVersion = useAppSelector((state) => state.organization.orgDataVersion);
  const { message } = AntApp.useApp();

  // Load organizations and invitations when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadData = async () => {
      dispatch(setLoading(true));
      try {
        const [orgs, invitations, setups] = await Promise.all([
          organizationsApi.getOrganizations(),
          organizationsApi.getMyInvitations(),
          vaultsApi.getPendingSetups().catch(() => []),
        ]);
        dispatch(setOrganizations(orgs));
        dispatch(setMyInvitations(invitations));
        dispatch(setPendingSetups(setups));
      } catch (err) {
        const errorMsg = friendlyError(err, t, 'errors.loadDataFailed');
        dispatch(setError(errorMsg));
        void message.error(errorMsg);
      } finally {
        dispatch(setLoading(false));
      }
    };

    void loadData();
  }, [isAuthenticated, dispatch, message, t]);

  // Derive stable invitation-check key from current org's role + settings
  const currentOrgInviteKey = useMemo(() => {
    const currentOrg = organizations.find((o) => o.id === currentOrgId);
    if (!currentOrg) return '';
    return `${currentOrg.membership.role}:${String(currentOrg.settings.allowMemberInvites)}`;
  }, [organizations, currentOrgId]);

  // Load members and org invitations when current org changes
  useEffect(() => {
    if (!isAuthenticated || !currentOrgId) {
      dispatch(setMembers([]));
      dispatch(setOrgInvitations([]));
      return;
    }

    // Admins/owners can always view; members can view if allowMemberInvites is enabled
    const currentOrg = organizations.find((o) => o.id === currentOrgId);
    const canViewInvitations =
      currentOrg && canInvite(currentOrg.membership.role, currentOrg.settings.allowMemberInvites);

    const loadOrgData = async () => {
      // Load members (any role can view)
      try {
        const members = await organizationsApi.getMembers(currentOrgId);
        dispatch(setMembers(members));
      } catch (err) {
        console.error('Failed to load members:', err);
      }

      // Load org invitations separately (admin/owner only, may fail for others)
      if (canViewInvitations) {
        try {
          const invitations = await organizationsApi.getOrgInvitations(currentOrgId);
          dispatch(setOrgInvitations(invitations));
        } catch {
          // Expected for non-admin users — silently set empty
          dispatch(setOrgInvitations([]));
        }
      } else {
        dispatch(setOrgInvitations([]));
      }
    };

    void loadOrgData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentOrgId, currentOrgInviteKey, orgDataVersion, dispatch]);

  // Load vault tag definitions when org changes so every view (cards, list
  // rows, vault header, modals) reads from a single Redux source of truth.
  useEffect(() => {
    if (!isAuthenticated || !currentOrgId) {
      dispatch(setTagDefinitions([]));
      return;
    }
    vaultsApi
      .getVaultTags(currentOrgId)
      .then((tags) => dispatch(setTagDefinitions(tags)))
      .catch(() => {
        /* tags are optional */
      });
  }, [isAuthenticated, currentOrgId, dispatch]);

  // Load signing requests when org changes (single API call, stored in Redux)
  const loadSigningRequests = useCallback(async () => {
    if (!currentOrgId) {
      dispatch(setSigningRequests([]));
      return;
    }
    try {
      const results = await vaultsApi.getSigningRequests(currentOrgId);
      dispatch(setSigningRequests(results));
    } catch {
      // Silently fail
    }
  }, [currentOrgId, dispatch]);

  useEffect(() => {
    void loadSigningRequests();
  }, [loadSigningRequests]);

  const handleMenuToggle = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const handleSidebarClose = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  // Sidebar is visible on desktop always, on mobile only when toggled
  const isSidebarVisible = isMobile ? sidebarVisible : true;

  return (
    <WalletConnectProvider>
      <div className="app-layout">
        <Header onMenuToggle={handleMenuToggle} />
        <Sidebar
          isVisible={isSidebarVisible}
          isMobileOpen={isMobile && sidebarVisible}
          onClose={handleSidebarClose}
        />
        <main className={`app-main ${isMobile ? 'sidebar-collapsed' : ''}`}>
          <DemoBanner />
          <PastDueBanner />
          <Outlet />
        </main>
        <WalletConnectModals />
      </div>
    </WalletConnectProvider>
  );
}

export default AppLayout;
